/**
 * Computes the SHA-256 checksum of a file, hex-encoded.
 *
 * Used to let the backend verify uploaded model files were not corrupted or
 * tampered with in transit (see `apiUploadModelFile` / `apiUpdateModelFile`
 * in `util/network/Models.ts`).
 */
export const sha256 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    // crypto.subtle is only exposed in secure contexts (HTTPS or localhost);
    // fail loudly instead of throwing an uncatchable error inside onload.
    if (!crypto?.subtle) {
      reject(new Error("Web Crypto is unavailable (requires a secure context)"));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const buffer = e.target?.result;
      if (!(buffer instanceof ArrayBuffer)) {
        reject(new Error("Could not read file contents for checksum"));
        return;
      }
      crypto.subtle
        .digest("SHA-256", buffer)
        .then((hash) => {
          const hex = Array.from(new Uint8Array(hash))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
          resolve(hex);
        })
        .catch(reject);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("Failed to read file"));
    };
    reader.readAsArrayBuffer(file);
  });
};
